import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Seo } from '../../components/seo';
import { useLocale, useT } from '../../i18n/locale-context';
import { LocaleLink } from '../../i18n/locale-link';
import { localePath } from '../../i18n/locale-path';
import { ipContent } from '../../content/landings';
import { fetchLandingStats, landingStatsQueryKey } from '../../lib/landing-client';
import { trackLandingCta, trackLandingView } from '../../lib/landing-tracking';
import { formatCount, tendersWord } from '../../lib/format';
import { LandingHeader } from './layout/landing-header';
import { LandingFooter } from './layout/landing-footer';
import { StickyCta } from './layout/sticky-cta';
import { LandingSection } from './layout/landing-section';
import { LotCard } from './sections/lot-card';
import { NicheSwitcher } from './sections/niche-switcher';
import { PricingSingle } from './sections/pricing-single';
import { Faq } from './sections/faq';
import { Trust } from './sections/trust';
import { SourcesStrip } from './sections/sources-strip';

/**
 * `/ip` и `/uz/ip` — конверсионный лендинг тарифа ИП: начинающим поставщикам
 * помельче Pro, тендеры до 100 млн сум.
 *
 * Тем же принципам, что и `/start` (см. докстринг в `start.tsx`): своя шапка
 * и футер вне `AppLayout`, страница пререндерится. Разметка следует макету
 * `docs/superpowers/specs/2026-08-14-landings-mockups/landing-ip.html`.
 *
 * У этого тарифа нет годовой подписки (`ip-yearly` в сиде — `active: false`),
 * поэтому в отличие от `/start` здесь нет переключателя периода: см.
 * `<PricingSingle>`.
 */
export function LandingIp() {
  const { locale } = useLocale();
  const t = useT();
  const c = ipContent(locale);

  const { data } = useQuery({
    queryKey: landingStatsQueryKey(locale),
    queryFn: () => fetchLandingStats(locale),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    trackLandingView('ip', locale);
  }, [locale]);

  const cta = (label: string) => trackLandingCta('ip', locale, label);

  // Та же подстановка, что на /start (см. отступление 9 в start.tsx) — здесь
  // плейсхолдеры живут в finalLead, а не в finalH2. Без данных не рисуем всю
  // фразу вовсе (та же дисциплина, что и на /start, и что sections/counters.tsx
  // применяет к плиткам) — «появилось 0 новых тендеров» было бы ложным
  // утверждением, а не отсутствием данных.
  const finalLead = data
    ? c.finalLead
        .replace('{count}', formatCount(data.newLastWeek))
        .replace('{word}', tendersWord(data.newLastWeek, locale))
    : null;

  const lotCardLabels = {
    lockLabel: t('landing.lotCard.lockLabel'),
    activeLabel: t('landing.lotCard.activeLabel'),
    daysLeftTemplate: t('landing.lotCard.daysLeft'),
    budgetLabel: t('list.col.budget'),
    bidSecurityLabel: t('landing.lotCard.bidSecurityLabel'),
    deadlineLabel: t('tender.deadline.closes'),
    contactLabel: t('landing.lotCard.contactLabel'),
    docsLabel: t('landing.lotCard.docsLabel'),
  };

  return (
    <div className="min-h-screen bg-bone text-ink">
      <Seo
        title={c.pageTitle}
        description={c.metaDescription}
        canonical="/ip"
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: c.faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      {/* Отступление 10/12 (ревью владельца): шапка ведёт на /catalog, не на
          #pricing; телефон в шапке /ip не показывался и раньше. */}
      <LandingHeader
        nav={c.nav}
        ctaLabel={c.ctaHeader}
        ctaHref="/catalog"
        onCtaClick={() => cta('header')}
      />

      {/* ============ 1. Первый экран ============ */}
      <section id="hero" className="pb-11 pt-[52px]">
        <div className="mx-auto grid max-w-[1120px] gap-10 px-5 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <span className="mb-3 inline-block text-[12.5px] font-extrabold uppercase tracking-[0.08em] text-brand">
              {c.heroKicker}
            </span>
            <h1 className="mb-3.5 text-[clamp(30px,5.4vw,47px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
              {c.heroH1}
            </h1>
            <p className="max-w-[560px] text-[clamp(16.5px,2.2vw,19px)] text-ink-soft">
              {c.heroLead}
            </p>
            {/* См. комментарий у той же пары кнопок в start.tsx: на телефоне
                во всю ширину и в столбик, главная сверху. */}
            {/* Отступление 10 (ревью владельца, 2026-08-18): ведёт на
                /catalog, не /plans — только кнопка в блоке «Цена» продаёт
                тариф. */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <LocaleLink
                to="/catalog"
                onClick={() => cta('hero')}
                className="block w-full rounded-lg border-[1.5px] border-brand bg-brand px-6 py-3.5 text-center text-base font-bold text-white no-underline transition-colors hover:border-brand-mid hover:bg-brand-mid sm:inline-block sm:w-auto"
              >
                {c.ctaPrimary}
              </LocaleLink>
              <a
                href="#lots"
                onClick={() => cta('hero-lots')}
                className="block w-full rounded-lg bg-brand-light px-6 py-3.5 text-center text-base font-bold text-brand no-underline transition-colors hover:bg-[#DFEBE9] sm:inline-block sm:w-auto"
              >
                {c.ctaSecondary}
              </a>
            </div>
            <p className="mt-3 text-[13.5px] text-ink-soft">{c.heroMicro}</p>
          </div>

          {/* «Телефонный» мокап из макета: входящее уведомление → живой лот
              из data.starterLot (стартовая полоса бюджета — /ip продаёт тариф
              ИП, потолок 100 млн, поэтому здесь НЕ data.featuredLot: тот
              снимает /start и всегда крупная стройка на сотни млн, читателю
              /ip не по карману, см. landing-stats.repository.ts starterLot())
              → исходящая реплика «мне по силам». Именно третье сообщение —
              точка, ради которой существует секция: читатель узнаёт в
              карточке лот своего размера. Композируем вручную, а не через
              LandingIllustration (kind: 'chat') — тот тип держит только
              плоский список сообщений и не умеет вставить внутрь живую
              карточку. */}
          <div className="mx-auto w-full max-w-[420px] rounded-[20px] border border-edge bg-bone-surface p-4 shadow-lg lg:mx-0 lg:justify-self-end">
            <div className="mb-[9px] max-w-[92%] rounded-xl border border-edge bg-bone px-[13px] py-2.5 text-[13.5px] leading-[1.45]">
              <b className="mb-0.5 block">{c.heroChatNotice.title}</b>
              {c.heroChatNotice.body}{' '}
              <small className="text-ink-muted">· {c.heroChatNotice.time}</small>
            </div>

            {data?.starterLot && (
              <div className="mb-[9px]">
                <LotCard
                  lot={data.starterLot}
                  onClick={() => cta('hero-tender')}
                  {...lotCardLabels}
                />
              </div>
            )}

            <div className="ml-auto max-w-[92%] rounded-xl bg-brand-light px-[13px] py-2.5 text-[13.5px] leading-[1.45]">
              {c.heroChatReply}
            </div>
          </div>
        </div>
      </section>

      {/* ============ Источники ============ */}
      <SourcesStrip
        label={c.sourcesLabel}
        sources={c.sources}
        moreLabel={c.sourcesMoreLabel}
        updateNote={c.sourcesUpdateNote}
        sourceCount={data?.sourceCount}
      />

      {/* ============ 1b. Каждый пятый — без победителя ============ */}
      {/* Отступление 8 (ревью владельца, 2026-08-18): раньше жил ВНУТРИ секции
          «Что вы умеете делать?», после переключателя ниш. Владелец хотел
          прямее — заметный стат-блок ДО неё, сразу под полосой источников:
          конкуренция ниже, чем кажется, это первое, что читатель узнаёт про
          рынок, а не вывод после просмотра ниш. */}
      <LandingSection top="lg">
        <div className="flex items-center gap-4 rounded-xl border border-edge bg-brand-light p-5 text-[15px]">
          <div className="flex-none text-[38px] font-extrabold leading-none text-brand">
            {c.factBig}
          </div>
          <div className="text-ink">{c.factBody}</div>
        </div>
      </LandingSection>

      {/* ============ 2. Выбор ниши ============ */}
      <LandingSection id="lots" top="sm">
        <span className="mb-3 inline-block text-[12.5px] font-extrabold uppercase tracking-[0.08em] text-brand">
          {c.lotsKicker}
        </span>
        <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
          {c.lotsTitle}
        </h2>
        <p className="max-w-[680px] text-[clamp(16.5px,2.2vw,19px)] text-ink-soft">{c.lotsLead}</p>

        <NicheSwitcher
          tabs={c.niches}
          niches={data?.niches ?? []}
          foundTemplate={c.foundTemplate}
          seeAllLabel={c.seeAllLabel}
          emptyLabel={t('landing.nicheSwitcher.emptyLabel')}
          onClick={cta}
          {...lotCardLabels}
        />

        <div className="mt-[18px] flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <LocaleLink
            to="/catalog"
            onClick={() => cta('lots')}
            className="block w-full rounded-lg border-[1.5px] border-brand bg-brand px-6 py-3.5 text-center text-base font-bold text-white no-underline transition-colors hover:border-brand-mid hover:bg-brand-mid sm:inline-block sm:w-auto"
          >
            {c.lotsCta}
          </LocaleLink>
        </div>
        <p className="mt-3 text-[13.5px] text-ink-soft">{c.lotsMicro}</p>
      </LandingSection>

      {/* ============ 3. Три сомнения ============ */}
      <LandingSection id="fears" top="sm">
        <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
          {c.fearsTitle}
        </h2>
        <p className="max-w-[680px] text-[clamp(16.5px,2.2vw,19px)] text-ink-soft">{c.fearsLead}</p>
        <div className="mt-[26px] grid gap-4 md:grid-cols-3">
          {c.fears.map((f) => (
            <div
              key={f.q}
              className="rounded-2xl border border-edge bg-bone-surface p-[22px] shadow-m-sm"
            >
              <div className="mb-3.5 border-l-[3px] border-brand pl-3 text-[14.5px] italic text-ink-soft">
                {f.q}
              </div>
              <h3 className="mb-1.5 text-[17.5px] font-extrabold leading-[1.12]">{f.title}</h3>
              <p className="text-[14.5px] text-ink-soft">{f.body}</p>
            </div>
          ))}
        </div>
      </LandingSection>

      {/* ============ 4. Что даёт подписка ============ */}
      <LandingSection id="value" top="sm">
        <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
          {c.valueTitle}
        </h2>
        <div className="mt-[26px] grid gap-4 md:grid-cols-2">
          {c.values.map((v) => (
            <div
              key={v.title}
              className="rounded-2xl border border-edge bg-bone-surface p-6 shadow-m-sm"
            >
              <h3 className="mb-2 text-[18px] font-extrabold leading-[1.12]">{v.title}</h3>
              <p className="text-[15px] text-ink-soft">{v.body}</p>
            </div>
          ))}
        </div>
      </LandingSection>

      {/* ============ 5. Как начать ============ */}
      <LandingSection id="start" top="sm">
        <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
          {c.stepsTitle}
        </h2>
        {/* Карточка несёт короткий заголовок и одну строку — на телефоне номер
            встаёт в строку с заголовком (как в блоке «Что меняет Pro» на
            /start), и карточка перестаёт быть втрое выше своего содержимого.
            С sm возвращается раскладка макета: номер над заголовком. */}
        <div className="mt-[26px] grid gap-2.5 sm:grid-cols-2 sm:gap-3.5 lg:grid-cols-4">
          {c.steps.map((s, i) => (
            <div
              key={s.title}
              className="rounded-2xl border border-edge bg-bone-surface p-4 shadow-m-sm sm:p-5"
            >
              <div className="mb-1 flex items-center gap-2.5 sm:mb-0 sm:block">
                <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-brand text-sm font-extrabold text-white sm:mb-2.5">
                  {i + 1}
                </span>
                <h3 className="text-base font-extrabold leading-[1.12] sm:mb-1">{s.title}</h3>
              </div>
              <p className="text-[13.5px] text-ink-soft">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <LocaleLink
            to="/guide"
            onClick={() => cta('steps')}
            className="block w-full rounded-lg bg-brand-light px-6 py-3.5 text-center text-base font-bold text-brand no-underline transition-colors hover:bg-[#DFEBE9] sm:inline-block sm:w-auto"
          >
            {c.stepsCta}
          </LocaleLink>
        </div>
        <p className="mt-3 text-[13.5px] text-ink-soft">{c.stepsMicro}</p>
      </LandingSection>

      {/* ============ 6. Цена ============ */}
      <PricingSingle
        title={c.pricingTitle}
        plan={c.plan}
        planMicro={c.planMicro}
        mathTitle={c.mathTitle}
        mathRows={c.mathRows}
        mathMicro={c.mathMicro}
        upsell={c.upsell}
        onCtaClick={cta}
      />

      {/* ============ 7. Кто за этим стоит ============ */}
      <Trust title={c.teamTitle} members={c.team} fineprint={c.fineprint} onActionClick={cta} />

      {/* ============ 8. FAQ ============ */}
      <Faq title={c.faqTitle} items={c.faq} />

      {/* ============ 9. Финал ============ */}
      <LandingSection top="sm">
        <div className="rounded-2xl bg-ink px-7 py-[52px] text-center">
          <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em] text-white">
            {c.finalH2}
          </h2>
          {finalLead && <p className="mx-auto mt-2.5 max-w-[600px] text-white/75">{finalLead}</p>}
          {/* Отступление 10: единственная кнопка — вторая («Смотреть тендеры
              до 100 млн сум») уже вела на /catalog, и после переадресации
              первой туда же стала дублем; убрана целиком (см. types.ts). */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
            <LocaleLink
              to="/catalog"
              onClick={() => cta('final')}
              className="block w-full rounded-lg border-[1.5px] border-white bg-white px-6 py-3.5 text-center text-base font-bold text-ink no-underline transition-colors hover:border-bone hover:bg-bone sm:inline-block sm:w-auto"
            >
              {c.finalCtaPrimary}
            </LocaleLink>
          </div>
        </div>
      </LandingSection>

      <LandingFooter links={c.footerLinks} legal={c.footerLegal} />
      {/* Отступление 10: ведёт на /catalog; StickyCta рендерит обычный <a> без
          LocaleLink (см. sticky-cta.tsx) — локаль подставляем здесь. */}
      <StickyCta
        title={c.stickyTitle}
        subtitle={c.stickySubtitle}
        cta={c.stickyCta}
        href={localePath('/catalog', locale)}
        watchId="hero"
        onClick={() => cta('sticky')}
      />
    </div>
  );
}
